import type { RefObject, TargetedKeyboardEvent, TargetedPointerEvent } from 'preact';
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';

interface Drag { from: number; pointerId: number; startY: number; offsetY: number; to: number | null }

/**
 * Drag-handle and arrow-key reordering for a short editable list. A drag previews with a lifted
 * row and a drop line; `apply` receives the new order (old indexes) only when the item is dropped.
 */
export function useListReorder({ count, root, rowSelector, gripSelector, apply, describe }: {
  count: number; root: RefObject<HTMLElement | null>; rowSelector: string; gripSelector: string;
  apply: (order: number[]) => void; describe: (from: number, to: number) => string;
}) {
  const [drag, setDrag] = useState<Drag | null>(null);
  const [message, setMessage] = useState('');
  const focusGrip = useRef<number | null>(null);
  const rest = (from: number) => Array.from({ length: count }, (_, i) => i).filter(i => i !== from);
  const reorder = (from: number, to: number) => {
    const others = rest(from);
    if (to < 0 || to > others.length) return;
    const order = [...others.slice(0, to), from, ...others.slice(to)];
    if (order.every((j, i) => j === i)) return;
    // Set before apply: a synchronous re-render must still find where focus belongs.
    focusGrip.current = to;
    apply(order);
    setMessage(describe(from, to));
  };
  const target = (clientY: number, from: number) => {
    const rows = [...(root.current?.querySelectorAll<HTMLElement>(rowSelector) ?? [])].filter((_, i) => i !== from);
    const at = rows.findIndex(row => { const box = row.getBoundingClientRect(); return clientY < box.top + box.height / 2; });
    return at < 0 ? rows.length : at;
  };
  useEffect(() => {
    if (!drag) return;
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); setDrag(null); } };
    const cancel = () => setDrag(null);
    window.addEventListener('keydown', escape, true); window.addEventListener('blur', cancel);
    return () => { window.removeEventListener('keydown', escape, true); window.removeEventListener('blur', cancel); };
  }, [!!drag]);
  // Moving an item changes which row holds it; keep focus on the handle of the item that moved.
  useLayoutEffect(() => {
    if (focusGrip.current === null) return;
    root.current?.querySelectorAll<HTMLButtonElement>(gripSelector)[focusGrip.current]?.focus({ preventScroll: true });
    focusGrip.current = null;
  });

  const dragging = (i: number) => drag?.from === i && drag.to !== null;
  const row = (i: number) => {
    let drop: 'before' | 'after' | undefined;
    if (drag && drag.to !== null && i !== drag.from) {
      const others = rest(drag.from);
      drop = others[drag.to] === i ? 'before' : drag.to === others.length && others.at(-1) === i ? 'after' : undefined;
    }
    return { dragging: dragging(i), drop, style: dragging(i) ? { transform: `translateY(${drag!.offsetY}px)` } : undefined };
  };
  const grip = (i: number) => ({
    onPointerDown: (event: TargetedPointerEvent<HTMLButtonElement>) => {
      if (!event.isPrimary || event.button !== 0 || drag || count < 2) return;
      event.preventDefault(); event.currentTarget.focus(); event.currentTarget.setPointerCapture(event.pointerId);
      setDrag({ from: i, pointerId: event.pointerId, startY: event.clientY, offsetY: 0, to: null });
    },
    onPointerMove: (event: TargetedPointerEvent<HTMLButtonElement>) => {
      if (drag?.pointerId !== event.pointerId) return;
      const offsetY = event.clientY - drag.startY;
      if (drag.to === null && Math.abs(offsetY) < 4) return;
      setDrag({ ...drag, offsetY, to: target(event.clientY, drag.from) });
    },
    onPointerUp: (event: TargetedPointerEvent<HTMLButtonElement>) => {
      if (drag?.pointerId !== event.pointerId) return;
      if (drag.to !== null) reorder(drag.from, target(event.clientY, drag.from));
      setDrag(null);
    },
    onPointerCancel: () => setDrag(null),
    onLostPointerCapture: () => setDrag(null),
    onKeyDown: (event: TargetedKeyboardEvent<HTMLButtonElement>) => {
      if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
      event.preventDefault(); reorder(i, i + (event.key === 'ArrowUp' ? -1 : 1));
    },
  });
  return { row, grip, message };
}
